import React from "react";
import { TIPO_DISPLAY } from "../utils/case_taxonomy";

export const TIPO_LABEL = {
  tutela: "Tutela",
  fallo: "Fallo",
  requerimiento: "Requerimiento",
  desacato: "Desacato",
  sancion: "Sanción",
  consulta: "Consulta",
};

export const TIPO_BADGE = {
  tutela:        "info",
  fallo:         "warning",
  requerimiento: "mint",
  desacato:      "danger",
  sancion:       "warning",
  consulta:      "muted",
};

export const ESTADO_LABEL = {
  nuevo: "Nuevo",
  pendiente_insumos: "Pendiente insumos",
  insumos_solicitados: "Insumos solicitados",
  expediente_solicitado: "Expediente solicitado",
  insumos_recibidos: "Insumos recibidos",
  en_elaboracion: "En elaboración",
  en_auditoria: "En auditoría",
  con_observaciones: "Con observaciones",
  aprobado: "Aprobado",
  en_litisoft: "En LITISOFT",
  pendiente_reparto_lawyer: "Listo para reparto",
  asignado_abogado: "Con abogado",
  fallo_pendiente_revision: "Pendiente revisión",
  pendiente_revision_impugnacion: "Pendiente revisión impugnación",
  fallo_decidido_no_impugnar: "No impugnar",
  fallo_decidido_impugnar: "Impugnar",
  devuelto_a_reparto: "Devuelto a Reparto",
  cerrado: "Cerrado",
  error_marcado: "Marcado por error",
  // feature/lifecycle-redesign.
  pendiente_archivar: "Por archivar",
  archivado:          "Archivado",
  // feature/sancion-lifecycle. 9-state machine.
  sancion_emitida:               "Sanción emitida",
  sancion_en_consulta:           "En consulta",
  sancion_revoca_en_consulta:    "Revoca en consulta",
  sancion_confirma_en_consulta:  "Confirma en consulta",
  sancion_nulidad_reinicia:      "Nulidad / reinicia",
  sancion_obedece_revoca:        "Obedece — revoca",
  sancion_obedece_confirma:      "Obedece — confirma",
  sancion_archivo:               "Archivo",
  sancion_inaplicacion_archivo:  "Inaplicación / archivo",
  // Sprint 50 legacy sanciones states (backfilled away; kept for any audit UI)
  sancion_aboca_consulta:        "En consulta (Sprint 50)",
  sancion_confirma:              "Confirma (Sprint 50)",
  sancion_revoca:                "Revoca (Sprint 50)",
  sancion_nulidad:               "Nulidad (Sprint 50)",
  sancion_inaplicacion:          "Inaplicación (Sprint 50)",
};

const ESTADO_BADGE = {
  nuevo: "muted",
  pendiente_insumos: "warning",
  insumos_solicitados: "warning",
  expediente_solicitado: "warning",
  insumos_recibidos: "info",
  en_elaboracion: "info",
  en_auditoria: "warning",
  con_observaciones: "danger",
  aprobado: "success",
  en_litisoft: "mint",
  pendiente_reparto_lawyer: "info",
  asignado_abogado: "mint",
  fallo_pendiente_revision: "warning",
  pendiente_revision_impugnacion: "warning",
  fallo_decidido_no_impugnar: "muted",
  fallo_decidido_impugnar: "info",
  devuelto_a_reparto: "warning",
  cerrado: "success",
  error_marcado: "danger",
  // feature/lifecycle-redesign.
  pendiente_archivar: "mint",
  archivado:          "muted",
  // feature/sancion-lifecycle.
  sancion_emitida:               "warning",
  sancion_en_consulta:           "info",
  sancion_revoca_en_consulta:    "info",
  sancion_confirma_en_consulta:  "warning",
  sancion_nulidad_reinicia:      "warning",
  sancion_obedece_revoca:        "mint",
  sancion_obedece_confirma:      "danger",
  sancion_archivo:               "muted",
  sancion_inaplicacion_archivo:  "danger",
  // Sprint 50 legacy
  sancion_aboca_consulta:        "info",
  sancion_confirma:              "warning",
  sancion_revoca:                "info",
  sancion_nulidad:               "warning",
  sancion_inaplicacion:          "danger",
};

export function TipoPill({ tipo }) {
  return <span className={"badge " + (TIPO_BADGE[tipo] || "muted")}>{TIPO_LABEL[tipo] || tipo}</span>;
}

// feature/tipo-fallo-humanization.
// Renders case_type_data.tipo_fallo as a human-readable badge so list rows
// surface the resultado at a glance instead of "Fallo · pendiente". Color
// is meaning-driven (mint=favorable, danger=desfavorable, info=nulidad,
// muted=NO_PEDIR_INSUMO / null). Sprint 55's TipoFalloTabs (the
// favorable/desfavorable bucket filter) keeps using the raw enum via
// TIPO_FALLO_GROUP — different concern, kept separate.
export const TIPO_FALLO_LABEL = {
  FAVORABLE_1A:    "Favorable 1ª inst.",
  FAVORABLE_2A:    "Favorable 2ª inst.",
  DESFAVORABLE_1A: "Desfavorable 1ª inst.",
  DESFAVORABLE_2A: "Desfavorable 2ª inst.",
  NULIDAD:         "Nulidad",
  NO_PEDIR_INSUMO: "No pedir insumo",
};

const TIPO_FALLO_BADGE = {
  FAVORABLE_1A:    "mint",
  FAVORABLE_2A:    "mint",
  DESFAVORABLE_1A: "danger",
  DESFAVORABLE_2A: "danger",
  NULIDAD:         "info",
  NO_PEDIR_INSUMO: "muted",
};

export function humanizeTipoFallo(value) {
  if (!value) return "Sin clasificar";
  return TIPO_FALLO_LABEL[value] || value;
}

export function TipoFalloPill({ value, missingLabel = "Sin clasificar" }) {
  if (!value) {
    return <span className="badge muted" style={{ fontStyle: "italic" }}>{missingLabel}</span>;
  }
  const tone = TIPO_FALLO_BADGE[value] || "muted";
  const label = TIPO_FALLO_LABEL[value] || value;
  return <span className={"badge " + tone}>{label}</span>;
}

// feature/estado-prefer-subtipo. When a case is sitting in a
// generic workflow state (en_elaboracion / pendiente_insumos / insumos_*
// / expediente_solicitado) AND has a sub_tipo set, the sub_tipo label
// (e.g. "Cierre", "Apertura") is what Reparto/Angie actually want to see
// in the estado column — the workflow state is uninformative until the
// case advances. Once the case reaches a meaningful state (cerrado,
// asignado_abogado, sancion_*, fallo_*), the workflow label wins again.
//
// Color coding stays driven by the workflow estado; only the LABEL flips.
export const PREFER_SUB_TIPO_STATES = new Set([
  "en_elaboracion",
  "pendiente_insumos",
  "insumos_solicitados",
  "expediente_solicitado",
]);

export function formatEstadoDisplay(c) {
  if (!c?.estado) return "—";
  if (c.sub_tipo && PREFER_SUB_TIPO_STATES.has(c.estado)) {
    return TIPO_DISPLAY[c.sub_tipo] || c.sub_tipo;
  }
  return ESTADO_LABEL[c.estado] || c.estado;
}

// feature/contestada-tag. Visible in list views (Reparto,
// Camilo's dispatcher, lawyer case lists) so contestada status is
// scannable without entering the case detail. Reuses Sprint 50's
// contestada_at column — no new schema.
//
// feature/marcado-error-visual — Z2. When the case is in error_marcado,
// "Pendiente" was misleading (Angie's report 2026-05-09): the case has
// been retired by an error flag, so contestada is moot. Render "—"
// matching SlaPill's existing behavior on this estado (Pills.js:235).
// Callers that already pass `c={c}` everywhere should switch to
// `estado={c.estado}` here — single optional prop keeps the existing
// signature backward-compatible (legacy two-arg callers keep their
// current "Pendiente" fallback).
export function ContestadaTag({ contestadaAt, estado, dateFormat = "short" }) {
  if (estado === "error_marcado") {
    return <span className="muted" style={{ fontSize: 12 }}>—</span>;
  }
  if (contestadaAt) {
    const d = new Date(contestadaAt);
    const label = dateFormat === "short"
      ? d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit", year: "2-digit" })
      : d.toLocaleString("es-CO");
    return (
      <span className="badge mint" style={{ fontSize: 10, whiteSpace: "nowrap" }} title="Caso contestado">
        ✓ {label}
      </span>
    );
  }
  return (
    <span className="badge muted" style={{ fontSize: 10, whiteSpace: "nowrap" }} title="Caso no contestado todavía">
      Pendiente
    </span>
  );
}
// feature/estado-prefer-subtipo. EstadoPill now accepts either:
//   * <EstadoPill estado="cerrado" />               (legacy — workflow only)
//   * <EstadoPill estado="cerrado" sub_tipo="..." /> (preferred — uses sub_tipo
//                                                    label in PREFER_SUB_TIPO_STATES)
//   * <EstadoPill c={caseRow} />                    (preferred — pass full case row)
// Color coding always uses the workflow estado regardless of the displayed label.
//
// For pendiente_insumos / insumos_solicitados / expediente_solicitado with a
// sub_tipo set, render both pieces of info as a compound label
// ("Cierre · Pendiente insumos") so lawyers can see the pipeline state at a
// glance without entering the case detail. en_elaboracion keeps the pure
// sub_tipo override.
const COMPOUND_INSUMOS_PIPELINE_STATES = new Set([
  "pendiente_insumos",
  "insumos_solicitados",
  "expediente_solicitado",
]);

function estadoPillRender(row) {
  if (!row?.estado) return { label: "—", tone: "muted" };
  const wf = row.estado;
  if (row.sub_tipo && COMPOUND_INSUMOS_PIPELINE_STATES.has(wf)) {
    const subLabel = TIPO_DISPLAY[row.sub_tipo] || row.sub_tipo;
    const wfLabel  = ESTADO_LABEL[wf] || wf;
    return {
      label: `${subLabel} · ${wfLabel}`,
      tone:  ESTADO_BADGE[wf] || "muted",
    };
  }
  return {
    label: formatEstadoDisplay(row),
    tone:  ESTADO_BADGE[wf] || "muted",
  };
}

export function EstadoPill({ estado, sub_tipo, c }) {
  const row = c || { estado, sub_tipo };
  const { label, tone } = estadoPillRender(row);
  return <span className={"badge " + tone}>{label}</span>;
}

// SLA pill — server is source of truth for termino_horas + fecha_ingreso.
// feature/termino-hours — read termino_horas (integer) directly. Falls back to legacy
// termino string for any case still in the old format until migration runs.
// feature/insumos-validity — when sla_paused_at is set, show a gray "Pausado" pill and
// don't run the urgency math.
//
// feature/two-clock-architecture. Each case now carries two
// optional override timestamps:
//   * lawyer_deadline_at  — set when moderation extends a lawyer's plazo.
//   * insumos_deadline_at — set when moderation moves the insumos pipeline
//                           clock independently.
// When set, the override timestamp wins over the termino-based computation
// for that audience. Audience defaults to "lawyer" (lawyers and dispatchers
// drive vencida); pass audience="insumos" for the insumos-team dashboard.
function parseLegacyTermino(t) {
  if (!t) return null;
  const m = String(t).match(/(\d+)\s*(HORAS?|DIAS?)/i);
  if (!m) return null;
  const n = Number(m[1]);
  return /D/i.test(m[2]) ? n * 24 : n;
}
export function slaState(c, audience = "lawyer") {
  if (!c) return null;
  // Sprint 56 — terminal + by-angie states.
  if (c.estado === "archivado") return { kind: "done", label: "Archivado" };
  if (c.estado === "pendiente_archivar") return { kind: "ok", label: "Por archivar" };
  if (["cerrado", "aprobado", "en_litisoft", "fallo_decidido_no_impugnar", "error_marcado"].includes(c.estado)) return null;
  // Sprint 56 — Esperando insumo lock pauses the visible clock.
  if (c.esperando_insumo_at && !c.esperando_insumo_released_at) {
    return { kind: "paused", label: "Esperando insumo" };
  }
  // Sprint 56 — Timer 2 active (3h post-Esperando window). Use the
  // explicit deadline. Audience-agnostic.
  if (c.timer2_started_at && !c.timer2_ended_at && c.timer2_deadline_at) {
    const remaining = (new Date(c.timer2_deadline_at).getTime() - Date.now()) / 3600 / 1000;
    if (remaining < 0)  return { kind: "bad",  label: `Vencido ${Math.abs(remaining).toFixed(1)}h` };
    if (remaining < 1)  return { kind: "warn", label: `${remaining.toFixed(1)}h` };
    return { kind: "ok", label: `${remaining.toFixed(1)}h` };
  }
  // feature/marcar-contestada. Cases the lawyer has marked as
  // contestada are no longer running an SLA, even if for some reason the
  // estado transition didn't fire (defensive belt-and-suspenders).
  if (c.contestada_at) return null;
  if (c.sla_paused_at) return { kind: "paused", label: "Pausado" };

  // feature/two-clock-architecture — moderation override wins.
  const overrideKey = audience === "insumos" ? "insumos_deadline_at" : "lawyer_deadline_at";
  let due;
  if (c[overrideKey]) {
    due = new Date(c[overrideKey]).getTime();
  } else {
    const hours = c.termino_horas != null ? Number(c.termino_horas) : parseLegacyTermino(c.termino);
    if (!Number.isFinite(hours) || hours <= 0 || !c.fecha_ingreso) return null;
    const start = new Date(c.fecha_ingreso).getTime();
    const pausedSec = Number(c.sla_paused_total_seconds || 0);
    due = start + hours * 3600 * 1000 + pausedSec * 1000;
  }
  const remaining = (due - Date.now()) / 3600 / 1000;
  if (remaining < 0)  return { kind: "bad",  label: `Vencido ${Math.abs(remaining).toFixed(1)}h` };
  if (remaining < 4)  return { kind: "warn", label: `${remaining.toFixed(1)}h` };
  return { kind: "ok", label: `${remaining.toFixed(1)}h` };
}
export function SlaPill({ c, audience }) {
  const s = slaState(c, audience);
  if (!s) return <span className="muted" style={{ fontSize: 12 }}>—</span>;
  return <span className={"sla " + s.kind}>{s.label}</span>;
}
